import { capitalize } from 'lodash';

import { formatValue, getStringIdentifier, getValClassName } from './common';

import type { ChangelogDot } from './common';
import type {
  ChangelogDiff,
  ChangelogFormat,
  ChangelogSummaryCategory,
  ChangelogSummaryEntry,
  ChangelogSummaryRule,
  FormatterContext
} from './types';

const CATEGORY_ORDER: ChangelogSummaryCategory[] = [
  'added',
  'deleted',
  'restored',
  'updated',
  'groupsAdded',
  'groupsDeleted'
];

const CATEGORY_DOT: Record<ChangelogSummaryCategory, ChangelogDot> = {
  added: 'add',
  deleted: 'delete',
  groupsAdded: 'add',
  groupsDeleted: 'delete',
  restored: 'add',
  updated: 'edit'
};

function getCategoryMessage(category: ChangelogSummaryCategory, { formatMessage }: FormatterContext): string {
  const messages: Record<ChangelogSummaryCategory, string> = {
    added: formatMessage({ defaultMessage: 'added', id: 'changelogModal.message.added' }),
    deleted: formatMessage({ defaultMessage: 'deleted', id: 'changelogModal.message.deleted' }),
    groupsAdded: formatMessage({ defaultMessage: 'groups added', id: 'changelogModal.message.groupsAdded' }),
    groupsDeleted: formatMessage({ defaultMessage: 'groups deleted', id: 'changelogModal.message.groupsDeleted' }),
    restored: formatMessage({ defaultMessage: 'restored', id: 'changelogModal.message.restored' }),
    updated: formatMessage({ defaultMessage: 'updated', id: 'changelogModal.message.updated' })
  };
  return messages[category];
}

/** "field from X to Y" */
export function formatFieldEdition(field: string, change: ChangelogDiff, { formatMessage }: FormatterContext): string {
  return `${field} ${formatMessage({ defaultMessage: 'from', id: 'changelogModal.message.from' })} ${formatValue(
    change.lhs
  )} ${formatMessage({ defaultMessage: 'to', id: 'changelogModal.message.to' })} ${formatValue(change.rhs)}`;
}

/** First rule whose regex matches and whose format accepts the change */
export function findSummaryEntry(
  rules: ChangelogSummaryRule[],
  change: ChangelogDiff,
  context: FormatterContext
): (ChangelogSummaryEntry & Pick<ChangelogSummaryRule, 'order' | 'section'>) | undefined {
  const identifier = getStringIdentifier(change);
  for (const rule of rules) {
    const entry = rule.regex.test(identifier) ? rule.format(change, context) : undefined;
    if (entry) {
      return { ...entry, order: rule.order, section: rule.section };
    }
  }
  return undefined;
}

/** Groups the matching changes by category, one formatted line per category */
export function summarizeChanges(
  changes: ChangelogDiff[],
  rules: ChangelogSummaryRule[],
  context: FormatterContext
): ChangelogFormat[] {
  const entries = changes.flatMap(change => findSummaryEntry(rules, change, context) ?? []);
  return CATEGORY_ORDER.flatMap((category, index) => {
    const categoryEntries = entries.filter(entry => entry.category === category);
    if (!categoryEntries.length) {
      return [];
    }
    const labels = categoryEntries.map(entry => entry.label);
    return {
      description: (
        <div className={getValClassName(CATEGORY_DOT[category])}>
          <b>{`${capitalize(getCategoryMessage(category, context))} (${labels.length}) `}</b>
          <span>{labels.join(', ')}</span>
        </div>
      ),
      order: categoryEntries[0].order + index,
      section: categoryEntries[0].section
    };
  });
}
